import { useSuspenseQuery } from "@apollo/client/react";
import { Suspense } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import type { MutationRefetchQueries } from "~/common/types";
import { DetailLoading } from "~/components/loading";
import {
  Button,
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  Input,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Textarea,
} from "~/components/ui";
import { DataImportProjectsModelProvider, useDataImportProjectsModel } from "~/features/dataImportProjects/context";
import {
  GetDataImportProjectForFormDocument,
  type GetDataImportProjectForFormQuery,
  type GetDataImportProjectForFormQueryVariables,
} from "~/features/dataImportProjects/gqls";
import { useDataImportProjectEdit } from "~/features/dataImportProjects/hooks/useDataImportProjectEdit";
import { useTenant } from "~/features/tenant/context";

type DipProjectInfoFormValues = {
  name: string;
  statusId: string;
  description: string;
};

type Props = {
  projectId: string;
  readOnly: boolean;
  refetchQueriesOnEdit: MutationRefetchQueries;
  onCancel: () => void;
  onSaved: () => void;
};

/**
 * Inline, v2-styled (shadcn) editor for the DIP "Project info" panel. Edits the project name, status
 * and description.
 */
export function DipProjectInfoEditForm(props: Props) {
  return (
    <Suspense fallback={<DetailLoading />}>
      <DataImportProjectsModelProvider>
        <DipProjectInfoEditFormInner {...props} />
      </DataImportProjectsModelProvider>
    </Suspense>
  );
}

function DipProjectInfoEditFormInner({ projectId, readOnly, refetchQueriesOnEdit, onCancel, onSaved }: Props) {
  const { t } = useTranslation();
  const { tenantId } = useTenant();
  if (!tenantId) {
    throw new Error("Tenant ID is required");
  }
  const { dataImportProjectStatuses } = useDataImportProjectsModel();

  const { data } = useSuspenseQuery<GetDataImportProjectForFormQuery, GetDataImportProjectForFormQueryVariables>(
    GetDataImportProjectForFormDocument,
    {
      variables: { tenantId, id: projectId },
    },
  );
  const project = data.dataImportProjects[0];

  const { submit, processing } = useDataImportProjectEdit({
    projectId,
    refetchQueriesOnEdit,
    onSuccess: onSaved,
  });

  const form = useForm<DipProjectInfoFormValues>({
    defaultValues: {
      name: project?.name ?? "",
      statusId: project?.statusId ?? "",
      description: project?.description ?? "",
    },
  });

  if (!project) {
    return <p className="text-12-regular text-fg-3">{t("feature.common.noData")}</p>;
  }

  const onSubmit = (values: DipProjectInfoFormValues) => {
    if (readOnly) return;

    submit({
      name: values.name.trim(),
      statusId: values.statusId,
      documentUrl: project.documentUrl ?? "",
      description: values.description,
      references: [],
    });
  };

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
        <FormField
          control={form.control}
          name="name"
          render={({ field }) => (
            <FormItem>
              <FormLabel>{t("model.common.name")}</FormLabel>
              <FormControl>
                <Input {...field} />
              </FormControl>
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="statusId"
          render={({ field }) => (
            <FormItem>
              <FormLabel>{t("model.dataImportProject.status")}</FormLabel>
              <Select value={field.value} onValueChange={field.onChange}>
                <FormControl>
                  <SelectTrigger>
                    <SelectValue placeholder={t("model.dataImportProject.status")} />
                  </SelectTrigger>
                </FormControl>
                <SelectContent>
                  {dataImportProjectStatuses.map((status) => (
                    <SelectItem key={status.id} value={status.id}>
                      {status.id}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="description"
          render={({ field }) => (
            <FormItem>
              <FormLabel>{t("model.dataImportProject.description")}</FormLabel>
              <FormControl>
                <Textarea {...field} rows={4} />
              </FormControl>
            </FormItem>
          )}
        />

        <div className="flex justify-end gap-2 pt-1">
          <Button type="button" variant="ghost" size="sm" onClick={onCancel} disabled={processing}>
            {t("ui.cancel")}
          </Button>
          <Button type="submit" size="sm" disabled={processing || readOnly}>
            {t("ui.save")}
          </Button>
        </div>
      </form>
    </Form>
  );
}
